import { RgDataSetID, type RvData } from "@project-rouge/rg-core";
import { observer } from "mobx-react-lite";
import { useMemo, useState } from "react";
import {
  buildComponentCatalog,
  COMPONENT_DATASETS,
  groupByRgType,
  type ComponentEntry,
} from "src/screens/component-library-data";

// Admin screen for the kit of parts one level below a Module: the individual
// building components (walls, floors, doors, windows, beams, columns) from the
// RvData element registry. Components are grouped by rgType; click one for its
// full spec sheet (Revit family, material layers, thickness, size, usage).
// Doors/windows are flat elements, so this is a spec-sheet view, not a plan
// gallery like Module Explorer.

export const ComponentExplorerScreen = observer(function ComponentExplorerScreen() {
  const [datasetId, setDatasetId] = useState<RgDataSetID>(RgDataSetID.uk_01);
  const [selected, setSelected] = useState<ComponentEntry | null>(null);
  const [showInfo, setShowInfo] = useState(false);

  const groups = useMemo(
    () => groupByRgType(buildComponentCatalog(datasetId)),
    [datasetId]
  );
  const total = useMemo(
    () => groups.reduce((n, [, entries]) => n + entries.length, 0),
    [groups]
  );

  return (
    <div className="h-screen w-screen overflow-y-auto bg-white text-neutral-8 p-6 font-mono text-xs">
      <header className="flex flex-wrap items-center gap-3 mb-6">
        <h1 className="text-lg font-bold mr-4">Component library</h1>
        <div className="flex gap-1">
          {COMPONENT_DATASETS.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => setDatasetId(id)}
              className={`px-3 py-1 rounded ${
                id === datasetId
                  ? "bg-neutral-8 text-white"
                  : "bg-neutral-3 text-neutral-7 hover:bg-neutral-4"
              }`}
            >
              {id}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setShowInfo((v) => !v)}
          className="ml-auto px-3 py-1 rounded bg-neutral-3 text-neutral-7 hover:bg-neutral-4"
        >
          {showInfo ? "hide design notes" : "design notes"}
        </button>
        <span className="text-neutral-6">{total} components</span>
      </header>

      <p className="text-neutral-6 mb-4">
        The kit of parts below a module: each distinct building element from the RvData registry,
        identified by a <code>modulousId</code> (<code>category|type|partNumber</code>) and backed
        by a Revit family. Grouped by element type; click a component for its full spec sheet.
      </p>

      {showInfo && <DesignNotes />}

      {groups.map(([rgType, entries]) => (
        <section key={rgType} className="mb-6">
          <h2 className="text-sm font-bold mb-2 border-b border-neutral-3 pb-1">
            {rgType} <span className="text-neutral-5 font-normal">({entries.length})</span>
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {entries.map((entry) => (
              <ComponentCard
                key={entry.rv.modulousId}
                entry={entry}
                onClick={() => setSelected(entry)}
              />
            ))}
          </div>
        </section>
      ))}

      {selected && <ComponentModal entry={selected} onClose={() => setSelected(null)} />}
    </div>
  );
});

function DesignNotes() {
  return (
    <div className="border border-neutral-3 rounded bg-neutral-1 p-4 mb-6 max-w-3xl">
      <h2 className="text-sm font-bold mb-2">Design notes</h2>
      <ul className="list-disc pl-5 space-y-1.5 text-neutral-7">
        <li>
          <b>Source of truth is the RvData registry</b> (<code>RvDataListSets[dataset]</code>), not
          the apartment/module geometry. Each distinct component therefore appears exactly once —
          walking modules would surface thousands of duplicate instances of the same part.
        </li>
        <li>
          <b>Grouped by <code>rgType</code></b> (Wall, Floor, Door, Window, Beam, Column…), which is
          authoritative in the data. The <code>modCategory</code> code (WPA, DCD, WNF…) is shown as a
          sub-label rather than used for grouping, since there's no authoritative category→name map.
        </li>
        <li>
          <b>Spec-sheet view, not a plan gallery</b> (unlike Module Explorer). Doors and windows are
          essentially flat elements, so a top-down <code>SvgPlan</code> adds little. Instead each
          component shows a material-layer stack + metadata card.
        </li>
        <li>
          <b>The layer bar is proportional to layer thickness</b> (a rough wall-section view, e.g.
          <code> FCD|02|</code> = brick / air / insulation). When no layer carries a thickness, the
          bar falls back to equal-width segments.
        </li>
        <li>
          <b>Usage count</b> = number of times this <code>modulousId</code> is instantiated across
          the selected dataset's apartments. "unused" means the part is registered in RvData but no
          apartment references it.
        </li>
        <li>
          <b>Size is <code>[width, height, depth]</code> in metres</b>; a <code>–</code> axis means
          that dimension is unlocked (driven by placement, not fixed by the part).
        </li>
        <li>
          <b>Admin/dev tool conventions:</b> URL-only (no nav link), hard-coded English (no i18n),
          dataset switchable between uk_01 and uk_02 — mirrors the Module Explorer and cost-tracking
          screens.
        </li>
      </ul>
    </div>
  );
}

function ComponentCard({ entry, onClick }: { entry: ComponentEntry; onClick: () => void }) {
  const { rv, usageCount } = entry;
  return (
    <button
      type="button"
      onClick={onClick}
      className="border border-neutral-3 rounded p-3 flex flex-col text-left hover:border-neutral-6"
    >
      <div className="flex items-center gap-2 mb-2">
        <Swatch color={rv.color} />
        <span className="font-semibold truncate" title={rv.modulousId}>
          {rv.modulousId}
        </span>
      </div>
      <span className="text-neutral-6 truncate" title={rv.rvFamilyType}>
        {rv.rvFamilyType}
      </span>
      <LayerStack layers={rv.layers} />
      <div className="flex justify-between text-neutral-5 mt-2">
        <span>{rv.totalThickness != null ? `${fmtM(rv.totalThickness)} m` : "—"}</span>
        <span>{usageCount > 0 ? `×${usageCount}` : "unused"}</span>
      </div>
    </button>
  );
}

function ComponentModal({ entry, onClose }: { entry: ComponentEntry; onClose: () => void }) {
  const { rv, usageCount } = entry;
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-8 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg max-w-2xl w-full max-h-full overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 min-w-0">
            <Swatch color={rv.color} />
            <h3 className="text-base font-bold truncate">{rv.modulousId}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-2 py-1 rounded bg-neutral-3 hover:bg-neutral-4 shrink-0"
          >
            close
          </button>
        </div>

        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 mb-4">
          <Row label="Category" value={rv.modCategory} />
          <Row label="Type" value={rv.modType} />
          <Row label="Part number" value={rv.modPartNumber || "—"} />
          <Row label="rg types" value={rv.rgTypes.join(", ")} />
          <Row label="Revit family" value={rv.rvFamilyName} />
          <Row label="Revit type" value={rv.rvFamilyType} />
          <Row label="Total thickness" value={rv.totalThickness != null ? `${fmtM(rv.totalThickness)} m` : "—"} />
          <Row label="Size (w×h×d)" value={fmtSize(rv.size)} />
          <Row label="Usage" value={usageCount > 0 ? `×${usageCount}` : "unused"} />
        </dl>

        <h4 className="text-sm font-bold mb-2 border-t border-neutral-3 pt-3">
          Material layers
        </h4>
        {!rv.layers || rv.layers.length === 0 ? (
          <p className="text-neutral-5 italic">No layer data.</p>
        ) : (
          <>
            <LayerStack layers={rv.layers} />
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2">
              {rv.layers.map((layer, i) => (
                <Row
                  key={i}
                  label={layer.name}
                  value={layer.thickness != null ? `${fmtM(layer.thickness)} m` : "—"}
                />
              ))}
            </dl>
          </>
        )}
      </div>
    </div>
  );
}

// Horizontal bar of material layers, widths proportional to thickness when
// available. Falls back to equal segments if no layer has a thickness.
function LayerStack({ layers }: { layers: RvData["layers"] }) {
  if (!layers || layers.length === 0) return null;
  const withThickness = layers.filter((l) => l.thickness != null);
  const totalT = withThickness.reduce((n, l) => n + (l.thickness ?? 0), 0);
  return (
    <div className="flex h-3 w-full mt-2 rounded overflow-hidden border border-neutral-3">
      {layers.map((layer, i) => {
        const pct =
          totalT > 0 && layer.thickness != null
            ? (layer.thickness / totalT) * 100
            : 100 / layers.length;
        return (
          <div
            key={i}
            title={`${layer.name}${layer.thickness != null ? ` · ${fmtM(layer.thickness)}m` : ""}`}
            style={{ width: `${pct}%`, backgroundColor: layerColor(i) }}
          />
        );
      })}
    </div>
  );
}

function Swatch({ color }: { color?: string }) {
  return (
    <span
      className="inline-block w-4 h-4 rounded border border-neutral-4 shrink-0"
      style={{ backgroundColor: color || "transparent" }}
    />
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-neutral-6 truncate" title={label}>
        {label}
      </dt>
      <dd className="text-right tabular-nums break-words">{value}</dd>
    </>
  );
}

// Deterministic greyscale-ish palette so adjacent layers are distinguishable.
function layerColor(i: number): string {
  const shades = ["#c7c7c7", "#9a9a9a", "#e0e0e0", "#b0b0b0", "#808080", "#d0d0d0"];
  return shades[i % shades.length];
}

function fmtM(value: number): string {
  return value.toFixed(3);
}

function fmtSize(size?: RvData["size"]): string {
  if (!size) return "—";
  return size.map((v) => (v == null ? "–" : fmtM(v))).join(" × ") + " m";
}
